/**
 * EPIC 47 — pure logic for the app-bar MESSAGES menu (MessagesMenu.tsx).
 *
 * The unread count a signed-in user has on a single conversation depends on WHICH
 * side of the thread they sit on (the counter model is deliberately asymmetric —
 * see shared-types/conversation.ts):
 *   • staff (admin/dispatcher/finance) — the single shared `staffUnread`;
 *   • worker — the per-uid `unreadBy[uid]` (one fixed non-staff participant);
 *   • client — the single shared `clientUnread` (mutable client team).
 * Extracted + unit-tested because getting the counter wrong per viewpoint would
 * silently show a stale badge. Sorting (most-recently-active first) lives here too
 * so the menu and its tests agree on ordering.
 */

import type { Conversation } from '@rendimees24/shared-types';

/** Which side of the thread the current user sits on. */
export type MessagesViewpoint = 'staff' | 'worker' | 'client';

/** Milliseconds for an ISO string or a Firestore Timestamp-like value; 0 if absent. */
export function conversationMillis(v: unknown): number {
  if (
    v &&
    typeof v === 'object' &&
    'toMillis' in v &&
    typeof (v as { toMillis: () => number }).toMillis === 'function'
  ) {
    return (v as { toMillis: () => number }).toMillis();
  }
  if (typeof v === 'string') return Date.parse(v) || 0;
  return 0;
}

/** The current user's unread count on ONE conversation, per their viewpoint. */
export function conversationUnread(
  conv: Conversation,
  uid: string,
  viewpoint: MessagesViewpoint,
): number {
  switch (viewpoint) {
    case 'staff':
      return conv.staffUnread ?? 0;
    case 'worker':
      return (uid && conv.unreadBy?.[uid]) || 0;
    case 'client':
      return conv.clientUnread ?? 0;
    default:
      return 0;
  }
}

/** Σ unread across all the user's conversations — the app-bar badge count. */
export function totalUnread(
  conversations: Conversation[],
  uid: string,
  viewpoint: MessagesViewpoint,
): number {
  return conversations.reduce((sum, c) => sum + conversationUnread(c, uid, viewpoint), 0);
}

/**
 * Most-recently-active first, capped. Used to render the dropdown list — shows
 * BOTH unread ("active") and already-read threads, newest activity on top.
 */
export function recentConversations(
  conversations: Conversation[],
  cap: number,
): Conversation[] {
  return [...conversations]
    .sort((a, b) => conversationMillis(b.updatedAt) - conversationMillis(a.updatedAt))
    .slice(0, Math.max(0, cap));
}
